/**
  * @class mmodWorkflow
  * @description
  * Class implementing user interface for workflows.
  *
  * Example of use:
  * put in mOffice.js, startComponents():
  * ```
  * new mmodWorkflow("mmodInstructions", {workflow_server: ":msvrInstructions"})
  * ```
  *
  * If more customization needed, create derived class
  * ```
  * import mmodWorkflow from 'mmodWorkflow'
  * export default class mmodInstructions extends mmodWorkflow {
  * }
  * ```
  */

import mmodModule from 'mmodModule'
import msysWorkflow from 'msysWorkflow'
const mfs = require('msysFS')
const geo = require('msysGeo')

export default class mmodWorkflow extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: { type: 'object', default: { type: 'full' }, description: 'Initial window setup; same structure as required by `mmWindowSet`' },
      workflow_server: { type: 'string', description: 'Listener acting as the server-side for the workflow' },
      modal_actions: { type: 'boolean', description: 'if true, all action forms will be displayed as popup; default is true for mOffice and false for other applications', default: MOSY_APP==='mOffice' },
      suspended: { type: 'string', enum: ['', 'hidden', 'disabled'], description: 'how to report suspended state in mmStatus', default: MOSY_APP==='mOffice' ? 'hidden' : 'disabled' },
      max_objects: { type: 'number', default: 100, description: 'Maximum number of objects in the list/table' },
      tabular: { type: 'boolean', description: 'if true, module will work in filter+table mode, otherwise in list mode' },
      list_view: { type: 'string', description: 'id of view to be used for table/list; if empty, will be "table" if tabular=true and "list" if tabular=false' },
      filter_view: { type: 'string', description: 'id of view to be used for filter', default: 'filter' },
      delay_model_creation: { type: 'integer', description: 'delay of building GUI after receiving model', default: 100 },
      delay_watch_after_model: { type: 'integer', description: 'delay of watching data after model is received', default: 100 },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
    this.last_version = 0
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * generate ID on client side if configured
  */
  generateNewId() {
    const id_generator = _.get(this.workflow, 'id_generator')
    if(id_generator==='client-oid') {
      return this.oid()
    }else{
      return ''
    }
  } // generateNewId

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * returns phrase prefixed with wf.${workflow_id}. to allow proper trnaslation of the term
  */
  tr(phrase) {
    phrase = _.toString(phrase)
    if(phrase==='') return ''
    return 'wf.'+_.get(this, 'workflow.id', 'generic')+'.'+phrase
  } // tr

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm({ layout: 'sidesplit', class: 'wf' })

    this.subscribe('#KEYPRESS')

    this.mmStart()
    this.toggleModuleVisibility()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * shows/hides module from application bars according to workflow availability
  */
  toggleModuleVisibility() {
    if(this.workflow) {
      // show module
      this.publish('#ALL', 'mmStatus', { status: 'ok' })
    }else{
      // hide module
      this.publish('#ALL', 'mmStatus', { status: this.config.suspended })
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * workflow model definition
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id] - workflow ID
  * @param {integer} [params.version] - model version
  * @param {string} [params.icon] - SVG icon
  * @param {string} [params.md5] - MD5 checksum
  * @param {string} [params.id_generator] - indicates who and how generates id
  * @param {string} [params.sqlview] - definition of SQL query to retrieve read-only workflow
  * @param {string} [params.sqlview_log] - definition of SQL query to retrieve read-only workflow logs
  * @param {string} [params.notifications] - indicates whether to notify on object changes
  * @param {object} [params.fields] - workflow variables
  * @param {object} [params.sql_queries] - SQL queries used for calculation of fields
  * @param {object} [params.forms] - workflow GUI forms
  * @param {string} [params.translations] - workflow translations as CSV
  * @param {string} [params.scripts] - workflow scripts
  * @param {string} [params.styles] - workflow CSS styles
  * @param {array} [params.acls] - access control list
  * @param {object} [params.states] - workflow states and their actions
  */
  async mmModel(sender, params) {
    if(!params.id && params.md5!=='offline') {
      // deleted (not accessible) model
      delete this.list_view
      delete this.elList
      delete this.elFilter
      this.closeActionPopup()
      this.popupCloseDefault()
      this.el.deleteItems()
      delete this.elDetail
      delete this.workflow
      this.toggleNewButton()
      this.publish('#ALL', 'mmStatus', { status: this.config.suspended })
      this.publish(this.config.workflow_server, 'mmWatch') // stop any possible previous watching
      return
    }

    if(params.states) {
      // we received updated model definition
      mfs.setJSON('model_'+this.listener_id, params)
    }else{
      // we shall use cached model
      const new_params = mfs.getJSON('model_'+this.listener_id)
      if(!new_params || (params.md5!=='offline' && new_params.md5!==params.md5)) {
        return this.publish(this.config.workflow_server, 'mmModelGet')
      }else{
        params = new_params
      }
    }

    // updated model
    await asyncTimeout(this.config.delay_model_creation)

    // clean GUI
    const fragment = document.createDocumentFragment()
    delete this.list_view
    delete this.elList
    delete this.elFilter
    this.closeActionPopup()
    this.popupCloseDefault()
    this.el.deleteItems()
    delete this.elDetail

    // create workflow object
    this.workflow = new msysWorkflow(this, params)

    // setup GUI artefacts
    this.toggleNewButton()
    this.toggleModuleVisibility()

    if(params.icon) {
      this.el.setSvgIcon(this.listener_id, params.icon)
    }

    // register workflow translations
    LANG.copyTranslation(this.listener_id, [this.listener_id, `wf.${this.workflow.id}.Menu`, `wf.${this.workflow.id}.Title`])

    // setup workflow styles
    const styles_id = 'css_wf_'+this.workflow.id
    let elStyles = document.getElementById(styles_id)
    if(!elStyles) {
      elStyles = this.el.createElementFast('style')
      elStyles.type = 'text/css'
      elStyles.id = styles_id
      document.head.appendChild(elStyles)
    }
    elStyles.innerHTML = _.toString(this.workflow.styles)

    // inform others that module is ready
    this.publish('#ALL', 'mmStatus', { status: 'ok' })

    const tabular = this.config.tabular
    const list_key = this.config.list_view || (tabular ? 'table' : 'list')

    // setup filter panel if configured as tabular
    if(tabular) {
      const filter = this.workflow.forms[this.config.filter_view]
      if(filter) {
        filter.key = 'filter'
        filter.view = true
        filter.tag = filter.tag || 'mele-view'
        this.elFilter = fragment._insertChild(filter)
      }
    }

    // setup list of objects if configured
    const list = this.list_view = this.workflow.forms[list_key]
    if(list) {
      list.key = 'list'
      list.view = true
      list.tag = list.tag || ('mele-'+(tabular ? 'table' : 'list'))
      this.elList = fragment._insertChild(list)

      // TEST: add bulk actions for testing
      // if(this.elList.tagName === 'MELE-TABLE') {
      //   this.elList.bulk = [
      //     { key: 'test', label: 'Test Bulk', icon: 'wf.confirm', color: 'blue' },
      //   ]
      //   this.elList.eventBulkSelected = (self, bulkselected, old) => {
      //     console.log2('eventBulkSelected', { bulkselected, old })
      //   }
      // }

      fragment._insertChild({
        tag: 'mele-view',
        key: 'empty',
        view: true,
        unavailable: true,
        header: ' ',
      })
    }

    // append all created elements to DOM
    this.el._body.appendChild(fragment)

    // setup default view and split between right-top and right-bottom views
    this.el.views = tabular ? 'filter,list' : 'list,empty'
    if(list && list.splitheight) {
      this.el.splitheight = list.splitheight
    }
    this.el.layoutChanged()
    this.toggleNewButton()

    // start watching for data changes
    if(params.md5!=='offline') {
      this.getWatchAfterModelChange()
    }

    // call method that could be overwritten by derived classes
    // for tasks after model update
    this.modelUpdated(params)
  } // mmModel

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * called when workflow model has been updated
  * @param {object} params - mmModel parameters
  */
  modelUpdated(params) {
  } // modelUpdated

  getWatchAfterModelChange() {
    if(this.list_view && this.list_view.query_watch) {
      // perform search of objects (it will send mmWatch to server)
      const values = this.elFilter ? this.elFilter.getValues() : {}
      setTimeout(()=>{
        this.searchObjects(values)
      }, this.config.delay_watch_after_model)
    }else{
      // otherwise stop any possible previous watching
      this.publish(this.config.workflow_server, 'mmWatch')
    }
  } // getWatchAfterModelChange

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    this.loggedin_dt = Date.now()
    if(String(this.user_id??'') !== String(params.user_id??'')) {
      if(this.workflow) this.workflow.version = 0 // on user change we have to re-load model to get proper new actions
      // when user_id changes, update the list
      this.closeActionPopup()
      if(this.elList) this.elList.deleteItems()
    }
    if(params.user_id>0) {
      const cachedModel = mfs.getJSON('model_'+this.listener_id)
      const p = cachedModel && cachedModel.md5 ? { md5: cachedModel.md5 } : {}
      this.publish(this.config.workflow_server, 'mmModelGet', p) // watch for model changes
    } else { this.publish('#ME', 'mmModel') } // logout cleans up the model
    super.mmUserLogged(sender, params)
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server notifies watchers that some system users were updated
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - whether all fields or just subset is sent
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} params.version - version of data sent by server
  * @param {array} params.list - array of changed records
  */
  mmUsers(sender, params) {
    super.mmUsers(sender, params)
    if(this.elList) this.elList.getItemElements().forEach(el=>el.languageChanged()) // HACK: there is some problem with proper translation of items, so force re-translation
  } // mmUsers

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * toggle new button on/off depending on access rights
  */
  toggleNewButton(sender, params) {
    const new_actions = Object.keys(_.get(this, 'workflow.states..actions', {}))
    const new_hidden = new_actions.length > 0
    if(this.elFilter) this.elFilter.forAll('[action=new]', el=>{ el.hidden=!new_hidden })
    if(this.elList) this.elList.forAll('[action=new]', el=>{ el.hidden=!new_hidden })
  } // toggleNewButton

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Returns info structure of actor based on current module status
  */
  currentActor() {
    return {
      app_id: this.app_id,
      scope: '',
      listener_id: this.listener_id,
      request_id: '',
      ip: '',
      apptype: MOSY_APP,
      user_id: this.user_id,
      login: this.login,
      callsign: this.callsign,
      status: this.user_status,
    }
  } // currentActor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    if(!this.elList) return
    this.publish('#WF'+this.workflow.id.toUpperCase(), 'mmObjects', _.omit(params, ['request_id'])) // publish to any potential listeners of our change queue

    if(params.full) this.elList.deleteItems()

    const deletions = []
    const updates = []
    params.list.forEach(item=>{
      item._limited = !!params.limited // this indicates result is not complete, only some fields are included
      item._actions = item._actions || []
      if(item.state==='WIPED') {
        deletions.push(item.id)
      }else{
        const old = _.get(this.elList.getItem(item.id), 'data') || {}
        const missing_files = this.workflow.decodeFileFields(old, item)
        if(missing_files) {
          item._limited = true
        }

        // merge object logs
        const logs = {}
        const _logs = item._logs || []
        _logs.forEach(log=>{ logs[log.log_id]=log })
        item._logs = _.defaults(logs, old._logs)

        // merge old and new information
        if(item._limited) {
          _.defaults(item, _.omit(old, ['_logs', '_limited']))
        }

        this.computeCalculatedFields(item)

        this.sendNotification(item)

        updates.push({
          key: item.id,
          data: item,
        })
      }
    })

    this.elList.updateItems(updates)
    if(deletions.length>0) {
      if(deletions.indexOf(this.elList.selected)>=0) this.elList.selected = ''
      if(deletions.indexOf(this.id_to_be_selected)>=0) delete this.id_to_be_selected
      this.elList.deleteItems(deletions)
    }

    // delayed selection of ID, used by URL hash
    if(this.id_to_be_selected) {
      this.mmOpen(null, { id: this.id_to_be_selected })
      delete this.id_to_be_selected
      return
    }

    if(params.total > this.config.max_objects && this.elList.classList.contains('mele-table')) {
      this.elList.pagingtotal = params.total
      this.elList.pagingoffset = params.offset
      this.elList.pagingsize = params.limit
    } else if(params.total < this.config.max_objects) {
      this.elList.pagingtotal = 0
    }

    // edit popup is open, warn user if edited object has been changed
    this.updateDetail(this.elPopup, params.list, params.requested_id)
    this.updateDetail(this.elDetail, params.list, params.requested_id)

    if(params.full || params.version >= this.last_version) this.last_version = params.version

    // update language translation options if needed
    const shared_fields = {}
    for(const field in this.workflow.fields) {
      const def = this.workflow.fields[field]
      if(def.share) shared_fields[field] = def.share
    }

    if(Object.keys(shared_fields).length>0) {
      const items = this.elList.getItems()
      for(const field in shared_fields) {
        const options = { '': LANG.ZERO_SPACE }
        items.forEach(item=>{
          options[item.key] = item.data[field]
        })
        const changed = LANG.createOptions(shared_fields[field], options, true)
        if(changed) LANG.updateDependingElements(shared_fields[field])
      }
    }
  } // mmObjects

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Send notification to modules
  */
  sendNotification(item) {
    const currentId = this.elList.selected[0]
    if(item._notify && (!this.el.focused || String(item.id??'')!==String(currentId??''))) {
      const level = item._notify.substr(0, 1)
      const message = item._notify.substr(1)
      this.publish('#NOTIFICATIONS', 'mmNotify', {
        id: this.workflow.id+':'+item.id,
        priority: level>1 ? 10 : level==='1' ? 20 : 30,
        sound: this.el.focused ? '' : 'notification',
        message,
        icon: 'wf.'+this.workflow.id,
        position: 'right',
        actions: ['notification.show', 'form.close'],
      })
    }
  } // sendNotification

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * mmodNotifications informs other module that its notification button has been clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.id - id of the notification
  * @param {string} params.action - notificatin action
  */
  mmNotifyAction(sender, params) {
    this.publish('mmodNotifications', 'mmNotify', { id: params.id })
    if(params.action==='notification.show') {
      this.publish('msysWM', 'mmWindowFocus', { id: this.listener_id })
      this.publish(this.listener_id, 'mmOpen', { id: params.id.replace(/.*:/, '') })
    }
  } // mmNotifyAction

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * this method could be overwritten by derived classes to compute calculated properties
  * it is called just before update after received mmObjects or on any field change
  */
  computeCalculatedFields(obj) {
    const script = this.workflow.parsed_scripts?.computeCalculatedFields
    if(!script) return
    try{
      script.call(this, obj)
    }catch(e) {
      this.error('Error in WORKFLOW.computeCalculatedFields: '+e)
    }
  } // computeCalculatedFields

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * update form element with actual values from the list if there has been change for the displayed object
  */
  updateDetail(elDetail, changes, requested_id) {
    if(!elDetail) return
    const isPopup = elDetail.classList.contains('mele-popup')
    const active_id = String(elDetail.datakey??'')
    if(_.toString(active_id)==='') return
    const change = changes.find(change=>String(change.id??'')===active_id)
    if(!change) return // no change matching ID of currently displayed form
    if(change.state==='WIPED') {
      // object has been deleted
      if(isPopup) {
        this.closeActionPopup()
      }else {
        this.closeActionView()
      }
      const response_to_reply = requested_id && String(requested_id)===active_id
      const alert = !response_to_reply && isPopup // we alert user if this is change outside of our action in popup window
      if(alert) this.popupInfo(this.tr('ObjectDeleted'), this.tr('UpdateConflict'))
    }else{
      if(isPopup) {
        elDetail.setServerValues(change)
      }else{
        this.selectObject(active_id)
      }
    }
  } // updateDetail

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Ask module to open its window. The action is module specific, e.g. `mmodAppWall` shows/hides itself when receiving `mmOpen`/`mmClose` messages
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.view] - view to become active
  * @param {string} [params.id] - record to be displayed
  * @param {boolean} [params.replace] - indicates that current breadcrumb item shall be replaced
  */
  mmOpen(sender, params) {
    if(this.elPopup) this.closeActionPopup()
    this.selectObject(params.id)
  } // mmOpen

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * close action popup if open
  */
  closeActionPopup() {
    if(this.elPopup) {
      this.elPopup.deleteElement()
      delete this.elPopup
    }
  } // closeActionPopup

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * close action view if open
  */
  closeActionView() {
    // this.el.classList.remove('maximized')
    if(this.elDetail) {
      this.elDetail.deleteElement()
      delete this.elDetail
      this.el.views = this.config.tabular ? 'filter,list' : 'list,empty'
    }
  } // closeActionView

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * maximizes detail view
  */
  maximizeActionView() {
    this.el.classList.add('maximized')
  } // maximizeActionView

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * returns detail view from maximized to normal size
  */
  normalizeActionView() {
    this.el.classList.remove('maximized')
  } // normalizeActionView

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is submitted
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.button] - key attribute of the button
  * @param {string} [params.datakey] - key attribute of the content
  * @param {object} params.values - hash of key:value of contained editable inputs
  * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
  * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
  */
  mmFormSubmit(sender, params) {
    if(params.holder==='filter') {
      this.el.active = 1
      this.searchObjects(params.values)
    }else if(/^FORM:\w+:ACTION:\w+/.test(params.button)) {
      const [, form_name, action_name] = /^FORM:(\w+):ACTION:(\w+)$/.exec(params.button)
      this.performAction(form_name, action_name)
    }else if(/^ACTION:\w+/.test(params.button) && params.holder) {
      this.performAction(params.holder, params.button.replace(/^ACTION:/, ''))
    }
  } // mmFormSubmit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * perform search of objects
  * @param {object} values - search parameters
  */
  searchObjects(values) {
    if(!this.list_view || !this.elList) return
    if(this.list_view.query_watch==='new') this.elList.deleteItems() // clean list if only new records shall be displayed

    this.elList.selected=[]
    const query = {}
    let sort = []
    for(const field in values) {
      const value = _.toString(values[field])
      if(value!=='') {
        // use only non-empty values for search
        if(/^sort\d+$/.test(field)) {
          // special handling for fields to be used as sort criteria
          sort[field.substr(4)] = value
        }else{
          query[field] = value
        }
      }
    }

    let fields
    // construct list of fields required from server
    if(Array.isArray(this.list_view.query_fields)) {
      if(this.list_view.query_fields.find(f=>f.startsWith('!'))) {
        // there are some exlcude fields, so use all fields as basis for exclusions
        fields = Object.keys(this.workflow.fields)
        for(const f of this.list_view.query_fields) {
          if(f.startsWith('!')) {
            const exclude = f.substr(1)
            fields = fields.filter(ff=>ff!==exclude)
          }else {
            fields.push(f)
          }
        }
      }else{
        fields = this.list_view.query_fields.slice()
      }
      fields.push('_form')
      fields = _.uniq(fields)
    }else{
      if(this.elList.columns) {
        fields = this.elList.columns.map(col=>String(col.key??'').replace(/^#/, '')).filter(f=>f!=='')
        fields.push('_form')
      }else{
        fields = []
        const MUST_FIELDS = ['label', 'label2', 'value', 'icon', 'iconleft', 'color']
        MUST_FIELDS.forEach(attr=>{
          const val = _.toString(this.elList[attr])
          const used_fields = val.match(/\bdata\.(\w+)/g) || []
          used_fields.forEach(f=>fields.push(f.substr(5)))
        })
        fields.push('_form')
        fields = _.uniq(fields)
      }
    }

    // prepare list of sorting fields
    sort = sort.filter(s=>!!s)
      .join(',')
      .split(',')
      .filter(s=>/^\w+[+-]?$/.test(s))
      .map(s=>s.replace(/\+$/, ''))

    // prepare sort params for sorting in current list
    if(sort.length>0) {
      this.elList.sort = sort.map(c => {
        let column = c.indexOf('-') >-1 ? c.slice(0, -1) : c
        const i = _.get(this.workflow.fields[column], 'type', '')
        column += (i === 'id' || i === 'integer') ? '#' : ''
        column += c.indexOf('-') >-1? '-':''
        return `data.${column}`
      })
    }else{
      if(this.elList.tagName!=='MELE-LIST') this.elList.sort=[]
    }

    // construct parameters for server request
    const params = {
      version: this.list_view.query_watch==='new' ? Date.now() : 0,
      query: {
        ...query,
      },
      fields,
      offset: 0,
      limit: this.list_view.query_limit>0 ? this.list_view.query_limit : this.config.max_objects,
    }

    if(sort.length>0) params.sort = sort
    if(this.list_view.query_actions) params.actions = true
    if(this.list_view.query_logs) params.logs = true
    if(this.list_view.query_attachments) params.attachments = this.list_view.query_attachments

    const action = /^(all|new)$/.test(this.list_view.query_watch) ? 'mmWatch' : 'mmGet'
    this.last_action = action
    this.last_params = params
    if(action === 'mmWatch') delete params.offset
    this.serverRequest('SearchingObject', action, params)
  } // searchObjects

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-view> to parent module when changed paging by user
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {integer} params.offset - desired offset
  */
  mmViewPaging(sender, params) {
    this.warn('next offset', params.offset)
    this.last_params.offset = params.offset
    this.serverRequest('SearchingObject', 'mmGet', this.last_params)
  } // mmViewPaging

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * perform request to workflow server
  * @param {string} action - action to be invoked
  * @param {object} params - request parameters
  */
  async serverRequest(title, action, params) {
    if('_ttl' in params && (isNaN(params._ttl) || params._ttl<=0)) {
      delete params._ttl
    }
    title = title ? this.tr(title) : null
    let popup = null
    let popup_opener = null
    if(title) {
      popup_opener = setTimeout(()=>{
        popup_opener = null
        popup = this.popupWait(this.tr('PleaseWait'), title)
      }, 200)
    }
    try{
      const msg = await this.request(this.config.workflow_server, action, params, ['mmSuccess', 'mmObjects'])
      if(popup_opener) {
        clearTimeout(popup_opener)
        popup_opener = null
      }
      if(popup) {
        this.popupClose(popup)
        popup = null
      }
      if(msg.action==='mmObjects') this.mmObjects(msg.sender, { ...msg.params, requested_id: params.id })
      return msg
    }catch(e) {
      if(popup_opener) {
        clearTimeout(popup_opener)
        popup_opener = null
      }
      if(e?.action === 'mmUndelivered' && params._ttl > 0) {
        this.popupInfo(this.tr('MessageWillBeSentAfterReconnect'), title)
        if(popup) {
          this.popupClose(popup)
          popup = null
        }
      }else {
        this.popupError(this.tr(e.params?.error) ?? String(e), title)
      }
    }
  } // serverRequest

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by <mele-list> when selected item change
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of the list
  * @param {string} params.selected - key attribute of the selected items separated by ∫
  * @param {string} [params.old] - key attribute of previously selected item
  * @param {boolean} [params.interactive] - indicates if this was user activity
  */
  mmListSelected(sender, params) {
    if(this.printUrl) {
      URL.revokeObjectURL(this.printUrl)
      delete this.printUrl
    }
    if(params.holder==='list') return this.selectObject2(params.selected, true)
  } // mmListSelected

  selectObject(id) {
    id = _.toString(id)
    const current = _.toString(this.elList.selected[0])
    if(id===current) {
      this.selectObject2(id)
    }else {
      this.elList.selected = (id==='' ? [] : [id])
    }
  } // selectObject

  selectObject2(id, list_selected) {
    id = _.toString(id)
    if(this.window_active) this.el.updateUrlHash('#'+this.listener_id+'/'+id)
    const record = _.get(this.elList.getItem(id), 'data')
    if(record) {
      if(list_selected) record._limited = true
      const elItem = this.elList.getItemElement(id)
      if(elItem) {
        setTimeout(()=>{ elItem.scrollIntoViewIfNeeded() }, 10)
      }
      this.openAction(record, record._form, record._actions)
    }else{
      setTimeout(()=>{
        this.elList.scrollTop()
      }, 10)
      this.closeActionView()
    }
  } // selectObject2

  openAction(record, form_name, desired_actions) {
    form_name = _.toString(form_name)
    const form = this.workflow.forms[form_name]
    let isPopup = form ? form.modal : this.workflow.forms.noaccess.modal
    if(typeof isPopup==='undefined' && record._form!==form_name && this.config.modal_actions) {
      // special treatment; mOffice defaults to popup forms except default form
      isPopup = true
    }
    if(isPopup) {
      if(this.elPopup) return // popup window is already open and we want open new one, skip
      return this.openActionPopup(record, form_name, desired_actions)
    }else{
      return this.openActionView(record, form_name, desired_actions)
    }
  } // openAction

  buttonsForState(form_name, state, desired_actions) {
    const buttons = []
    const state_actions = _.get(this, 'workflow.states.'+state+'.actions')
    const groups = {}
    const popups = {}
    for(const action_name in state_actions) {
      if(desired_actions && desired_actions.indexOf(action_name)<0) continue // possible action not desired
      const action = state_actions[action_name]
      const hidden = action.color === 'hidden'
      const button = {
        tag: 'mele-button',
        type: 'actionbar',
        key: 'ACTION:'+action_name,
        color: action.color || '',
        action: 'submitvalid',
        class: 'noreadonly color-'+(action.color || ''),
        label: this.tr('action.'+action_name),
        hidden,
      }
      if(action.icon) {
        button.iconleft = action.icon
        button.label = ''
      }
      if(action.iconleft) {
        button.iconleft = action.iconleft
      }
      if(action.iconright) {
        button.iconright = action.iconright
      }
      if(action.group) {
        // button shall be part of grouped buttons
        let group = groups[action.group]
        if(!group) {
          // create group button if does not exists yet
          group = groups[action.group] = {
            tag: 'mele-group',
            key: 'GROUP:'+action.group,
            class: 'noreadonly buttongroup',
            propagate: true,
            items: [],
          }
          buttons.push(group)
        }

        // append action button to the group
        group.items.push(button)
      }else if(action.popup) {
        // button shall be part of grouped buttons
        let popup = popups[action.popup]
        if(!popup) {
          // create popup button if does not exists yet
          popup = popups[action.popup] = {
            type: 'actionbar',
            key: 'FORM:'+form_name+':ACTION:'+action.popup,
            eventButtonClicked: this.openButtonGroup,
            class: 'noreadonly buttonpopup',
            label: this.tr('action.'+action.popup),
            buttons: [],
          }
          buttons.push(popup)
        }

        // append action button to the group
        popup.buttons.push(button)
      }else{
        buttons.push(button)
      }
    }
    return buttons
  } // buttonsForState

  openButtonGroup() {
    const group_btn = this
    const elModal = group_btn.modalOpen({
      key: ':dropdown',
      position: 'above',
      outclick: ':modal.cancel',
      items: group_btn.buttons,
      class: 'wfactiongroupbuttons',
      eventSubmit: (form, key, values, btn)=>{
        if(btn.disabled) return
        const holder = group_btn.closest('.mele-container:not([propagate])')
        holder.eventSubmit(holder, holder.datakey, holder.getValues(), btn)
        elModal.modalClose()
      },
    })
  }

  openActionPopup(record, form_name, desired_actions) {
    this.closeActionPopup()
    let form = this.workflow.forms[form_name]
    if(!record || !form) return

    const formPosition = form.position
    form = _.extend({}, form, {
      tag: 'mele-popup',
      view: true,
      class: 'popupdialog wf'+(form.class?' '+form.class:''),
      position: formPosition || 'center',
      outclick: ':modal.cancel',
      eventCancel: ()=>{
        this.preventKeypress = true
        setTimeout(()=>{
          this.preventKeypress = false
        }, 100)
        if(!this.elPopup.changed) {
          this.elPopup.popupClose()
          delete this.elPopup
        } else {
          const elYesno = this.popupConfirm(this.tr('AreYouSureToDiscardChanges'), ()=>{
            elYesno.popupClose()
            this.elPopup.popupClose()
            delete this.elPopup
          })
        }
      },
    })

    if(!form.headerbuttons) form.headerbuttons = ['modalx']
    if(!form.footerbuttons) {
      form.footerbuttons = this.buttonsForState(form_name, record.state, desired_actions)
      form.footerbuttons.unshift(form.footerbuttons.length>0 ? 'popupcancel' : 'popupclose')
    }

    this.formHook('formopen', record, form_name)
    const elPopup = this.elPopup = this.popupOpen(form)
    elPopup.datakey = record.id
    this.formHook('formload', record, elPopup)
    elPopup.setValues(record)
    elPopup.commitValues()
    this.updateLogTable(elPopup, record._logs)
    this.updateComputed(elPopup)
    this.formHook('formready', record, elPopup)
    if(elPopup && elPopup._body && typeof elPopup._body.scrollIntoView === 'function') {
      setTimeout(()=>{
        elPopup._body.scrollIntoView({ behavior: 'smooth', block: 'start' })
        elPopup.focusinput()
      }, 50)
    }

    if(record.id && record._limited) {
      // object not fully loaded, request full details
      this.serverRequest('LoadingObject', 'mmGet', {
        version: 1, // this is to prevent return of "full" response
        query: { id: record.id },
        form: true,
        actions: true,
        logs: true,
      })
    }

    return elPopup
  } // openActionPopup

  openActionView(record, form_name, desired_actions) {
    form_name = String(form_name??'')
    if(!record) {
      this.closeActionView()
      return
    }

    let form = this.workflow.forms[form_name] || this.workflow.forms.noaccess

    form = _.extend({}, form, {
      view: true,
    })

    if(!form.tag) form.tag = 'mele-view'
    form.class = 'wf'+(form.class?' '+form.class:'')
    if(!form.headerbuttons && this.config.tabular) {
      form.headerbuttons = [
        { key: 'CLOSE', iconleft: 'sys.close', class: 'noreadonly' },
        { key: 'MAXIMIZE', iconleft: 'sys.maximize', class: 'noreadonly' },
        { key: 'NORMALIZE', iconleft: 'sys.minimize', class: 'noreadonly' },
      ]
    }
    if(!form.footerbuttons) {
      form.footerbuttons = this.buttonsForState(form_name, record.state, desired_actions)
      if(form_name !== String(record._form)) {
        // add "close" button if this is not default view form for current object
        form.footerbuttons.unshift({ key: 'RESET', color: 'red', label: `form.${form.footerbuttons.length>0 ? 'cancel' : 'close'}` })
      }
    }

    this.formHook('formopen', record, form_name)
    let elView
    let view_changed = false
    if(this.elDetail && String(this.elDetail.key??'')===String(form.key??'')) {
      elView = this.elDetail
      if(!(String(elView.datakey??'') === String(record.id??'') && record.id && record._limited)) elView.footerbuttons = form.footerbuttons
    }else{
      view_changed = true
      this.closeActionView()
      elView = this.elDetail = this.el.appendItems(form)
    }

    if(String(record.id??'') !== String(elView.datakey??'') || !/read/.test(form.key)) view_changed = true
    elView.datakey = record.id
    this.formHook('formload', record, elView)
    elView.setValues(record)
    elView.commitValues()
    // refresh headers/footers referencing @{field} placeholders right after data load
    elView.invokeDependentChanges('@')
    this.updateLogTable(elView, record._logs)
    this.updateComputed(elView)

    this.el.layout = 'sidesplit'
    this.el.views = (this.config.tabular ? 'filter,list,' : 'list,') + form.key
    this.el.layoutChanged()
    const arr = this.el.views.split(',')
    const active = parseInt(this.el.active || 0) + 1
    this.el.active = arr[active] ? active : arr.length - 1 // go to next
    this.formHook('formready', record, elView)

    const elWorkflows = this.el.findAll('@workflow').filter(el=>!el.closest('mele-tabs-wrapper'))
    if(elWorkflows.length>0) {
      try {
        const svg = this.workflow.getSVG(record, { lang: this.el.language })
        elWorkflows.forEach(el=>{ el.innerHTML=svg })
      } catch (error) {
        this.warn('Error generating workflow SVG', error)
      }
    }

    if(elView && elView._body &&typeof elView._body.scrollIntoView === 'function') {
      this.openActionViewScroll(elView)
    }
    if(record.id && record._limited) {
      // object not fully loaded, request full details
      this.serverRequest(view_changed ? 'LoadingObject' : null, 'mmGet', {
        version: 1, // this is to prevent return of "full" response
        query: { id: record.id },
        form: true,
        actions: true,
        logs: true,
      })
    }

    return elView
  } // openActionView

  openActionViewScroll(elView) {
    setTimeout(()=>{
      elView._body.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  updateLogTable(elForm, logs) {
    if(!elForm) return
    logs = logs || {}
    const logs_items = []
    const logs_rows = []
    let first_log = true
    for(const id in logs) {
      const log = _.clone(logs[id])
      log.key = id
      const note = _.toString(log.log_note).replace(/^\s+/, '').replace(/\s+$/, '')
      let details = note==='' ? '' : `<note>${note}</note>`
      const changes = []
      for(const field in log) {
        if(/^log_/.test(field)) continue
        let value = _.toString(log[field])
        if(first_log && value==='') continue
        const def = this.workflow.fields[field]
        if(!def) continue
        if(def && value!=='') {
          const itemlabel = _.toString(def.itemlabel)
          if(def.type==='geoshape') {
            const m = value.split(';')
            if(!_.isNil(m[0])) {
              value = geo.toLatLng(m[0]).join(', ')
              const approx = geo.approximateGeohash(m[0])
              if(approx) value += ` (${this.el.translate(approx)})`
            }
          }else if(def.type==='datetimeus') {
            value = _.usec2ymdhmsus(value)
          }else if(def.type==='jsonb') {
            try{
              if(typeof log[field] === 'object') {
                value = JSON.stringify(log[field], null, 2)
              }
            }catch(e) {
              // ignore parsing errors
            }
          }
          const prefix = _.toString(def.prefix).replace(/^\$wf\./, `wf.${this.workflow.id}.`)
          if(itemlabel!=='') {
            value = itemlabel.replace(/\$\{prefix\}/g, prefix).replace(/\$\{key\}/g, value)
            value = `<mele-text label="${value}"></mele-text>`
          }else if(prefix!=='') {
            value = `<mele-text label="${prefix}${value}"></mele-text>`
          }else if(value.length > 64) {
            value = value.substr(0, 64)+'...'
          }
        }
        changes.push(`<change><f><mele-text label="wf.${this.workflow.id}.${field}"></mele-text></f><v>${value}</v></change>`)
      }
      if(changes.length>0) details += `<changes>${changes.join('')}</changes>`
      if(details!=='') details = `<logdetails>${details}</logdetails>`
      log.log_details = details
      logs_items.push(log)
      logs_rows.push({ key: id, data: log })
      first_log = false
    }
    elForm.forAll('.mele-container[key=logs]', el=>{
      if(el.classList.contains('mele-table')) {
        el.setItems(logs_rows)
      }else{
        this.computeLogFields(logs_items)
        el.setItems(logs_items)
        el.getItemElements().forEach(el=>el.languageChanged()) // HACK: there is some problem with proper translation of items, so force re-translation
      }
    })
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * this method could be overwritten by derived classes to compute calculated properties
  * it is called just before setting values to a log view
  */
  computeLogFields() {
  } // computeLogFields

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  async mmButtonClicked(sender, params) {
    const holder = String(params.holder??'')
    const btn = params.button
    if(btn==='RESET') {
      if(this.elDetail && String(this.elDetail.key??'')===holder) {
        this.selectObject(this.elDetail.datakey)
      }
    }else if(btn==='export') {
      const title = this.tr('action.EXPORT')
      const server_url = _.toString(this.server_url).replace(/^ws:/, 'http:').replace(/^wss:/, 'https:').replace(/\/$/, '')
      if(!server_url) return this.popupError(this.tr(params.error), 'mmUndelivered')
      this.popupWait(this.tr('PleaseWait'), title)

      // prepare parameters for mmGetExport
      const req_params = {}
      try{
        const values = this.el.find('@{'+params.holder+'}').getValues()
        const query = {}
        let sort = []
        for(const field in values) {
          const value = _.toString(values[field])
          if(value!=='') {
            // use only non-empty values for search
            if(/^sort\d+$/.test(field)) {
              // special handling for fields to be used as sort criteria
              sort[field.substr(4)] = value
            }else{
              query[field] = value
            }
          }
        }

        req_params.query = query

        sort = sort.filter(s=>!!s).join(',').split(',').filter(s=>/^\w+[+-]?$/.test(s)).map(s=>s.replace(/\+$/, ''))
        if(sort.length>0) req_params.sort = sort

        req_params.fields = _.get(this.workflow.forms[params.holder], 'export_fields') || true
        req_params.language = this.el.language
      }catch(e) {}

      // send mmGetExport to workflow server listener
      try{
        const { params } = await this.request(this.config.workflow_server, 'mmGetExport', req_params, 'mmExport')
        this.popupCloseDefault()
        const doc = this.el.ownerDocument
        const el = this.el.createElementFast('a')
        el.setAttribute('target', '_blank')
        el.setAttribute('href', server_url+params.url)
        el.setAttribute('download', params.name)
        el.style.display = 'none'
        doc.body.appendChild(el)
        el.click()
        doc.body.removeChild(el)
      }catch(e) {
        this.popupError(String(e), e.name)
      }
    }else if(btn==='MAXIMIZE') {
      this.maximizeActionView()
    }else if(btn==='NORMALIZE') {
      this.normalizeActionView()
    }else if(btn==='CLOSE') {
      this.closeActionView()
    }
  } // mmButtonClicked

  async performAction(form_name, action, confirmed) {
    form_name = String(form_name??'')
    const elForm =
    form_name==='' ? null :
      this.elPopup && String(this.elPopup.key??'')===form_name ? this.elPopup :
        !this.elPopup && this.elDetail && String(this.elDetail.key??'')===form_name ? this.elDetail :
          null
    if(form_name!=='' && !elForm) return
    const id = _.get(elForm, 'datakey') || elForm.getValue('id') || this.generateNewId(elForm, action) // generate ID for new process
    let record = _.get(this.elList.getItem(id), 'data', { state: '' }) // if record does not exist, pretend it as new record
    record = _.cloneDeep(record) // especially important when a duplicate of original is created, to not overwrite data of original (e.g. remountClearUncopiedValues)
    const action_def = this.workflow.recordActionDefinition(record, action)
    if(action_def && action_def.client_script && this.workflow.parsed_scripts[action_def.client_script]) {
      try{
        const result = this.workflow.parsed_scripts[action_def.client_script].call(this, elForm, action)
        if(result===false) return
      }catch(e) {
        this.error('Problem with client_script function: '+e)
      }
    }
    let action_form_name = this.workflow.formForAction(record, action)
    if(action_form_name==null || action_form_name===undefined) return // do nothing if the action has no form
    action_form_name = String(action_form_name)
    if(action_form_name && action_form_name!==form_name) {
      // button pressed on different form than its action needs, so change the form
      this.openAction(record, action_form_name, [action])
    }else{
      // no form needed for the action, or we are already called from the form required; execute the action on server
      if(action_form_name && elForm) {
        // formsubmit hook could stop the processing of action
        // it just needs to set ctx.data.state = null
        await this.formHook('formsubmit', record, elForm)
        if(record.state === undefined || record.state === null) return
      }
      const values = action_form_name ? elForm.getValues(false, false, true) : {}
      const action_def = _.get(this, 'workflow.states.'+record.state+'.actions.'+action)
      if(!action_def) return
      if(action_def.confirm && !confirmed) {
        this.popupConfirm(typeof (action_def.confirm)==='string' ? action_def.confirm : this.tr('AreYouSure'), ()=>this.performAction(form_name, action, true))
        return
      }

      this.executeAction(action, id, record, values)
    }
  } // performAction

  async executeAction(action, id, record, values) {
    // transform integers with "" to null
    const fields = this.workflow.fields || {}
    for(const field in values) {
      const def = fields[field]
      if(def && def.type==='integer') {
        let val = parseInt(values[field])
        if(isNaN(val)) val = null
        values[field] = val
      }
    }

    this.workflow.encodeFileFields(record, values)

    const params = {
      id,
      action,
      params: values,
      _ttl: this.workflow?.states?.[record.state??'']?.actions?.[action]?.ttl,
    }

    const reply = await this.serverRequest(String(record.state??'')==='' ? 'CreatingObject' : 'UpdatingObject', 'mmAction', params)
    if(reply) {
      this.closeActionPopup()
      const new_id = _.get(reply, 'params.list[0].id', '')
      const new_state = _.get(reply, 'params.list[0].state', '')
      if(new_state!=='WIPED') {
        setTimeout(()=>this.selectObject(new_id), 20) // wait a bit to let list obtain new record
      }
    }
  } // executeAction

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when a new item on form is requested
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
  * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
  */
  mmFormNew(sender, params) {
    if(params.holder==='filter' || params.holder==='list') return this.newObject()
  } // mmFormNew

  async newObject() {
    const actions = Object.keys(_.get(this, 'workflow.states..actions', {}))
    if(actions.length===0) return this.popupError(this.tr('NewForbidden'), this.tr('NewItem'))
    const form_names = actions.map(action=>this.workflow.formForAction('', action)).filter(f=>f!==null && f!==undefined)
    const only_one_form = _.uniq(form_names).length===1
    const form_name = form_names[0]
    if(actions.length===1 && form_name==='') {
      // there is only one new action and does not need a form, so fire NEW action
      const new_id = this.generateNewId() // generate ID for new process

      const params = {
        id: new_id,
        action: actions[0],
        params: {},
        _ttl: this.workflow?.states?.['']?.actions?.[actions[0]]?.ttl,
      }
      const reply = await this.serverRequest('CreatingObject', 'mmAction', params)
      if(reply) {
        const new_id = _.get(reply, 'params.list[0].id', '')
        this.selectObject(new_id)
      }
    }else if(only_one_form) {
      // all "new" actions share the same form, so display the form with all the "new" actions as buttons
      this.openAction({ id: '', state: '' }, form_name, actions)
    }else if(form_names.length>0) {
      // there are several actions having different/none forms, so we have to decide
      // which form to display by letting user choose which "new" action he wants
      this.newObjectActionSelector(actions)
    }
  } // newObject

  newObjectActionSelector(actions) {
    const elSelector = this.popupOpen({
      class: 'popupdialog',
      header: this.tr('NewItem'),
      position: 'center',
      headerbuttons: ['modalx'],
      outclick: ':modal.cancel',
      items: [{
        tag: 'mele-list',
        // eslint-disable-next-line no-template-curly-in-string
        label: this.tr('action.${key}'),
        items: actions.map(action=>({ key: action })),
        eventSelected: (elList, selected)=>{
          const form_name = this.workflow.formForAction('', selected)
          elSelector.deleteElement()
          if(form_name==='') {
            this.performAction('', selected[0])
          }else if(String(form_name??'') !== '') {
            this.openAction({ id: '', state: '' }, form_name, selected)
          }
        },
      }],
    })
  } // newObjectActionSelector

  async formHook(hook, record, form) {
    const form_name = _.isObjectLike(form) ? form.key : form

    // special handling for print preview
    if(form_name === 'print_preview') {
      if(!record || !record.template) return
      this.remotePrint(record.template, record, form)
    }

    if(typeof this[hook] === 'function') {
      this[hook](form_name, record, form)
    }
    const steps = _.get(this.workflow.forms[form_name], hook)
    if(!steps) return
    try{
      const expr = this.workflow.stepsToExpression(steps)
      const runSteps = this.workflow.expressionToFunction({}, expr)
      const CTX = {
        dt: unow(),
        actor: {
          listener_id: this.listener_id,
          apptype: MOSY_APP,
          user_id: this.user_id,
          login: this.login,
          callsign: this.callsign,
          status: this.user_status,
        },
        action: '',
        source_state: _.get(record, 'state', ''),
        target_state: '',
        params: {},
        data: record,
        form,
      }
      await runSteps.call(this, CTX)
    }catch(e) {
      this.error('Error calling hook '+hook+': '+e.message)
    }
  } // formHook

  updateComputed(form) {
    const scripts = this.workflow.parsed_scripts || {}

    form.querySelectorAll('[recompute]').forEach(elComputed=>{
      try{
        let calc = /^MELE-/i.test(elComputed.tagName) ? elComputed.recompute : elComputed.getAttribute('recompute')
        if(/^\w+\(\)$/.test(calc)) calc = elComputed[calc.replace(/\(\)$/, '')]
        if(Array.isArray(calc)) calc = calc.join('\n')
        if(scripts[calc]) calc = scripts[calc]
        if(/^\{\{/.test(calc)) {
          elComputed.value = elComputed.translate(calc, true, 'recompute')
          return
        }
        // eslint-disable-next-line no-new-func
        const fcn = typeof (calc)==='function' ? calc : new Function('holder', 'emitter', calc)
        const holder = elComputed.parentElement.closest('.mele-container:not([propagate])')
        // eslint-disable-next-line no-unused-vars
        const WORKFLOW = scripts
        fcn.call(elComputed, holder, elComputed)
      }catch(e) {
        this.error('invalid recompute: '+e.message)
      }
    })

    form.querySelectorAll('[compute]').forEach(elComputed=>{
      try{
        let calc = /^MELE-/i.test(elComputed.tagName) ? elComputed.compute : elComputed.getAttribute('compute')
        if(/^\w+\(\)$/.test(calc)) calc = elComputed[calc.replace(/\(\)$/, '')]
        if(Array.isArray(calc)) calc = calc.join('\n')
        if(scripts[calc]) calc = scripts[calc]
        // eslint-disable-next-line no-new-func
        const fcn = typeof (calc)==='function' ? calc : new Function('holder', 'emitter', calc)
        const holder = elComputed.parentElement.closest('.mele-container:not([propagate])')
        const inputs = holder.findAll('.mele-input')
        // eslint-disable-next-line no-unused-vars
        const WORKFLOW = scripts
        inputs.forEach(el=>{
          if(elComputed===el) return
          try{
            fcn.call(elComputed, holder, el)
          }catch(e) {}
        })
      }catch(e) {
        this.error('invalid compute: '+e.message)
      }
    })
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    this.server_url = params.url
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * KeyDown event
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {string} [params.key] - pressed key value
   * @param {integer} [params.keyCode] - keycode value
   * @param {boolean} [params.repeat]
   * @param {boolean} [params.shift]
   * @param {boolean} [params.ctrl]
   * @param {boolean} [params.alt]
   * @param {boolean} [params.meta]
   */
  mmKeypress(sender, params) {
    const { key } = params

    if(!this.window_active) return
    if(this.elPopup || this.preventKeypress || this.el.parentElement.find('>mele-modal')) return

    const maximized = this.el.classList.contains('maximized')

    if(key==='Escape' && maximized && this.el.active===2) {
      // close maximized action view to see table of objects again
      this.closeActionView()
    }else if(key === 'ArrowUp') {
      // go to next/previous object in the list
      this.elList.selectPrevious()
    }else if(key === 'ArrowDown') {
      // go to next/previous object in the list
      this.elList.selectNext()
    }
  } // mmKeypress
} // mmodWorkflow
